import axios from 'axios'
import store from '@/store'

// Set the base URL for all API requests
axios.defaults.baseURL = process.env.VUE_APP_API_URL || '/';

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

// Add JWT authorization header if token exists
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Request interceptor - just pass through requests
axios.interceptors.request.use(
  config => config,
  error => Promise.reject(error)
);

// Track if we're currently logging out to prevent refresh attempts during logout
let isLoggingOut = false;

// Response interceptor
axios.interceptors.response.use(
  response => response,
  async error => {
    // If we're already logging out, don't attempt refresh
    if (isLoggingOut) {
      return Promise.reject(error);
    }

    // If there's no config or response, just reject the promise
    if (!error.config || !error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // These are the conditions under which we do NOT attempt to refresh the token:
    // 1. If it's not a 401 error
    if (error.response.status !== 401) {
      return Promise.reject(error);
    }

    // 2. If the request has already been retried
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // 3. If it's a request to an auth endpoint
    const isAuthEndpoint = [
      '/api/auth/login',
      '/api/auth/register',
      '/api/auth/logout',
      '/api/auth/refresh'
    ].some(endpoint => originalRequest.url.includes(endpoint));

    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    // 4. If we don't have a token
    if (!localStorage.getItem('token')) {
      return Promise.reject(error);
    }

    // 5. If the user is not logged in according to store state
    if (!store.getters['auth/isLoggedIn']) {
      return Promise.reject(error);
    }

    // Mark this request as retried
    originalRequest._retry = true;

    try {
      // Check if we already have a refresh in progress
      let refreshTokenPromise = store.state.auth.refreshTokenPromise;

      if (!refreshTokenPromise) {
        // Create a new promise to refresh the token
        refreshTokenPromise = store.dispatch('auth/refreshToken');
        store.commit('auth/SET_REFRESH_PROMISE', refreshTokenPromise);
      }

      // Wait for the token refresh to complete
      const newToken = await refreshTokenPromise;

      // Clear the promise reference
      store.commit('auth/SET_REFRESH_PROMISE', null);

      // Update the authorization header for the original request
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

      // Retry the original request
      return axios(originalRequest);
    } catch (refreshError) {
      console.error('Token refresh failed in interceptor:', refreshError);

      // Clear the refresh token promise
      store.commit('auth/SET_REFRESH_PROMISE', null);

      // Mark that we're logging out to prevent infinite loops
      isLoggingOut = true;

      // Clear authentication data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('lastRefreshAttempt');
      delete axios.defaults.headers.common['Authorization'];

      // Dispatch logout action with redirect to login
      try {
        // Use router to redirect to login page
        const router = require('@/router').default;

        // Dispatch silent logout first to clean up auth state
        await store.dispatch('auth/logout', { silent: true });

        // Then redirect to login page
        router.push('/login');
      } catch (e) {
        console.error('Error during logout after failed refresh:', e);
      }

      // Reset logging out flag after a short delay
      setTimeout(() => {
        isLoggingOut = false;
      }, 1000);

      return Promise.reject(error);
    }
  }
);

export default axios;